import React, { useEffect, useState, useMemo } from "react";
import {
  Flex,
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  IconButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  useColorModeValue,
  Spinner,
} from "@chakra-ui/react";
import { AddIcon, EditIcon, DeleteIcon, SearchIcon, ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import {
  getProduk,
  addProduk,
  updateProduk,
  deleteProduk,
} from "services/master/produkService";

export default function MasterProduk() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editing, setEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const toast = useToast();
  const textColor = useColorModeValue("gray.700", "white");

  const initialForm = {
    kd_kategori: "",
    nama_produk: "",
    hs_code: "",
  };
  const [form, setForm] = useState(initialForm);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getProduk();
      setData(res.data || []);
    } catch (err) {
      toast({
        title: "Gagal memuat data Produk",
        description: err.response?.data?.message || err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter Search
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;
    const lower = searchTerm.toLowerCase();
    return data.filter((item) => {
      const nama = item.nama_produk ? item.nama_produk.toLowerCase() : "";
      const kat = item.kd_kategori ? item.kd_kategori.toLowerCase() : "";
      const hs = item.hs_code ? item.hs_code.toLowerCase() : "";
      return nama.includes(lower) || kat.includes(lower) || hs.includes(lower);
    });
  }, [data, searchTerm]);

  // Pagination calculation
  const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const currentPagedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  const openAdd = () => {
    setForm(initialForm);
    setEditing(false);
    setEditingId(null);
    onOpen();
  };

  const openEdit = (row) => {
    setForm({
      kd_kategori: row.kd_kategori || "",
      nama_produk: row.nama_produk || "",
      hs_code: row.hs_code || "",
    });
    setEditing(true);
    setEditingId(row.id_produk);
    onOpen();
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing && editingId) {
        await updateProduk(editingId, form);
        toast({
          title: "Produk berhasil diubah",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      } else {
        await addProduk(form);
        toast({
          title: "Produk berhasil ditambah",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      }
      onClose();
      fetchData();
    } catch (err) {
      toast({
        title: "Gagal menyimpan",
        description: err.response?.data?.message || err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Yakin ingin menghapus produk ini?`)) return;
    try {
      await deleteProduk(id);
      toast({
        title: "Berhasil dihapus",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      fetchData();
    } catch (err) {
      toast({
        title: "Gagal menghapus",
        description: err.response?.data?.message || err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Card overflowX={{ sm: "scroll", xl: "hidden" }}>
        <CardHeader p="6px 0px 22px 0px" display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={3}>
          <Text fontSize="xl" color={textColor} fontWeight="bold">
            Master Produk
          </Text>
          <HStack spacing={3}>
            <InputGroup size="sm" w={{ base: "180px", md: "260px" }}>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Cari nama produk / kategori..."
                borderRadius="15px"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Button leftIcon={<AddIcon />} colorScheme="teal" size="sm" borderRadius="10px" onClick={openAdd}>
              Tambah Produk
            </Button>
          </HStack>
        </CardHeader>
        <CardBody>
          {loading ? (
            <Flex justify="center" p={8}>
              <Spinner size="xl" color="teal.500" />
            </Flex>
          ) : (
            <>
              <Table variant="simple" color={textColor}>
                <Thead>
                  <Tr my=".8rem" color="gray.400">
                    <Th color="gray.400">ID Produk</Th>
                    <Th color="gray.400">Kode Kategori</Th>
                    <Th color="gray.400">Nama Produk</Th>
                    <Th color="gray.400">HS Code</Th>
                    <Th color="gray.400">Aksi</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {currentPagedData.length === 0 ? (
                    <Tr>
                      <Td colSpan={5} textAlign="center" py={6}>
                        {searchTerm ? "Data tidak ditemukan sesuai pencarian" : "Tidak ada data"}
                      </Td>
                    </Tr>
                  ) : (
                    currentPagedData.map((row) => (
                      <Tr key={row.id_produk}>
                        <Td>{row.id_produk}</Td>
                        <Td>{row.kd_kategori || "-"}</Td>
                        <Td fontWeight="bold">{row.nama_produk}</Td>
                        <Td>{row.hs_code || "-"}</Td>
                        <Td>
                          <IconButton
                            aria-label="edit"
                            icon={<EditIcon />}
                            size="sm"
                            mr={2}
                            colorScheme="blue"
                            variant="ghost"
                            onClick={() => openEdit(row)}
                          />
                          <IconButton
                            aria-label="delete"
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => handleDelete(row.id_produk)}
                          />
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>

              {/* Pagination Controls */}
              <Flex justify="space-between" align="center" mt={4} flexWrap="wrap" gap={3}>
                <HStack spacing={2} align="center">
                  <Text fontSize="sm" color="gray.500">
                    Tampilkan
                  </Text>
                  <Select
                    size="sm"
                    w="80px"
                    borderRadius="8px"
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Select>
                  <Text fontSize="sm" color="gray.500">
                    dari {filteredData.length} data
                  </Text>
                </HStack>

                <HStack spacing={2}>
                  <IconButton
                    aria-label="prev"
                    icon={<ChevronLeftIcon />}
                    size="sm"
                    isDisabled={currentPage <= 1}
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  />
                  <Text fontSize="sm">
                    {currentPage} / {totalPages}
                  </Text>
                  <IconButton
                    aria-label="next"
                    icon={<ChevronRightIcon />}
                    size="sm"
                    isDisabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  />
                </HStack>
              </Flex>
            </>
          )}
        </CardBody>
      </Card>

      {/* Modal Add/Edit */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editing ? "Ubah Produk" : "Tambah Produk"}</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <FormControl isRequired mb={3}>
                <FormLabel>Kode Kategori</FormLabel>
                <Input
                  name="kd_kategori"
                  value={form.kd_kategori}
                  onChange={handleChange}
                  placeholder="Contoh: KTG-01"
                />
              </FormControl>
              <FormControl isRequired mb={3}>
                <FormLabel>Nama Produk</FormLabel>
                <Input
                  name="nama_produk"
                  value={form.nama_produk}
                  onChange={handleChange}
                  placeholder="Masukkan Nama Produk"
                />
              </FormControl>
              <FormControl mb={3}>
                <FormLabel>HS Code</FormLabel>
                <Input
                  name="hs_code"
                  value={form.hs_code}
                  onChange={handleChange}
                  placeholder="Masukkan HS Code"
                />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Batal
              </Button>
              <Button type="submit" colorScheme="teal">
                Simpan
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
